/**
 * Per-person avatar color helpers (v8 design language §4.5).
 *
 * v3 follow-up #3 (Item 2) — extended with a deterministic hash fallback
 * for initials that aren't in the founder roster (NR/DA/JP/MA). Previously
 * unrecognized initials all rendered grey (`bg-ink-2`); the new behaviour
 * gives every team member a stable color from a 12-entry palette indexed
 * by `hash(initials) % palette.length`. Same person → same color across
 * sessions and machines.
 *
 * Returns BOTH a className AND an inline-style payload:
 *   - Founders keep their themeable CSS-var class (so tokens.css overrides
 *     still work for the 4 names whose colors are part of brand identity).
 *   - Hash-fallback names get an empty class + an inline style. Tailwind
 *     JIT can't pick up dynamic arbitrary values like `bg-[#5b6dd7]` at
 *     build time, so the only reliable path is `style.background`.
 *
 * Avatar.tsx (and any other consumer) merges both.
 */

const PERSON_PALETTE = [
  // Founder colors first so any hash collision keeps the brand-aligned hue
  '#2d7d8c', // NR (teal)
  '#c47c2f', // DA (amber)
  '#2c6c5c', // JP (forest)
  '#d35f3a', // MA (coral)
  // 8 further palette entries — each visually distinct + WCAG-friendly
  // against the white initials text Avatar renders.
  '#5b6dd7',
  '#a3433a',
  '#3d8b6e',
  '#b6802e',
  '#7a5a8e',
  '#386676',
  '#5a7a3a',
  '#8b3d6e',
] as const;

export interface PersonColorResult {
  /** Tailwind class for founders (themable via CSS vars). Empty string otherwise. */
  className: string;
  /** Inline style for hash-fallback hues. Empty object for founders. */
  style: { background?: string };
}

export function personColor(name: string | null | undefined): PersonColorResult {
  if (!name?.trim()) return { className: 'bg-ink-3', style: {} };
  const pair = initials(name);
  switch (pair) {
    case 'NR':
      return { className: 'bg-[var(--person-nr,#2d7d8c)]', style: {} };
    case 'DA':
      return { className: 'bg-[var(--person-da,#c47c2f)]', style: {} };
    case 'JP':
      return { className: 'bg-[var(--person-jp,#2c6c5c)]', style: {} };
    case 'MA':
      return { className: 'bg-[var(--person-ma,#d35f3a)]', style: {} };
  }
  // djb2-ish hash — deterministic, fast, no allocations.
  let hash = 0;
  for (let i = 0; i < pair.length; i++) {
    hash = (hash << 5) - hash + pair.charCodeAt(i);
    hash |= 0;
  }
  const idx = Math.abs(hash) % PERSON_PALETTE.length;
  return { className: '', style: { background: PERSON_PALETTE[idx]! } };
}

/**
 * Backward-compat shim — callers passing the legacy class-only form still
 * get a usable className for the 4 founders. New code should call
 * `personColor()` and apply both `className` and `style`.
 */
export function personColorClass(name: string | null | undefined): string {
  const r = personColor(name);
  // Hash-fallback returns an empty class — fall back to a neutral
  // background-tinted class so consumers that don't apply the inline
  // style still see something instead of transparent. Avatar.tsx is
  // updated to use the structured form.
  return r.className || 'bg-ink-2';
}

export function initials(name: string | null | undefined): string {
  if (!name) return '?';
  return name
    .split(' ')
    .map((p) => p[0])
    .filter(Boolean)
    .join('')
    .toUpperCase()
    .slice(0, 2);
}
